import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiMessage, HandoffRow } from '../../../../core/api/api.types';
import { AttachmentsApiService } from '../../../../core/api/attachments-api.service';
import { OperatorSocketService } from '../../../../core/socket/operator-socket.service';

function uuid(): string {
  try { return crypto.randomUUID(); }
  catch { return `${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}

@Component({
  selector: 'op-case-chat',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    DatePipe,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './case-chat.component.html',
  styleUrl: './case-chat.component.scss',
})
export class CaseChatComponent implements OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly socket = inject(OperatorSocketService);
  private readonly attachmentsApi = inject(AttachmentsApiService);

  // ── Inputs ──────────────────────────────────────────────────────────────────
  @Input() messages: ApiMessage[] = [];
  @Input() firstUnreadMessageId: string | null = null;
  @Input() loading = false;
  @Input() demoMode = false;
  @Input() conversationId: string | null = null;
  @Input() activeHandoffId: string | null = null;
  @Input() customerName = 'Customer';
  @Input() customerInitials = 'C';
  @Input() customerPhone = '';
  @Input() primaryCaseCode = '—';
  @Input() pastOrdersRoute: any[] = ['/past-orders'];

  // ── Outputs ─────────────────────────────────────────────────────────────────
  @Output() muteAction = new EventEmitter<string>();
  @Output() openCreateOrderDrawer = new EventEmitter<void>();
  @Output() demoMessageSent = new EventEmitter<ApiMessage>();

  // ── Local state ─────────────────────────────────────────────────────────────
  readonly pendingAttachment = signal<{ id: string; name: string } | null>(null);
  readonly attachmentUploading = signal(false);
  readonly sending = signal(false);
  readonly sendError = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({ text: ['', [Validators.required]] });

  @ViewChild('messagesEl') private messagesEl?: ElementRef<HTMLElement>;
  @ViewChild('composerTextarea') private composerTextarea?: ElementRef<HTMLTextAreaElement>;

  private typingActive = false;
  private typingStopTimer: ReturnType<typeof setTimeout> | null = null;
  private prevConversationId: string | null = null;

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversationId']) {
      const convId = changes['conversationId'].currentValue as string | null;
      if (convId !== this.prevConversationId) {
        this.prevConversationId = convId;
        this.form.reset();
        this.pendingAttachment.set(null);
        this.sendError.set(null);
        this.stopTyping(true);
      }
    }

    if (changes['messages']) {
      const prev = changes['messages'].previousValue as ApiMessage[] | undefined;
      const curr = changes['messages'].currentValue as ApiMessage[];

      if (!prev || prev.length === 0 || changes['conversationId']) {
        // Defer scroll: #messagesEl is hidden while loading=true, the actual
        // scroll will fire once loading transitions to false (handler below).
        if (!this.loading) {
          const type = this.firstUnreadMessageId ? 'unread' : 'bottom';
          this.scheduleScroll(type, 'instant');
        }
      } else if (curr.length > prev.length) {
        if (this.isNearBottom()) this.scheduleScroll('bottom', 'smooth');
      }
    }

    // #messagesEl only exists while !loading. Trigger the initial scroll here
    // because messages arrive while loading=true and the earlier schedule is a no-op.
    if (changes['loading'] && !this.loading && this.messages.length > 0) {
      const type = this.firstUnreadMessageId ? 'unread' : 'bottom';
      this.scheduleScroll(type, 'instant');
    }

    if (changes['firstUnreadMessageId'] && this.firstUnreadMessageId && changes['messages']) {
      this.scheduleScroll('unread', 'instant');
    }
  }

  ngOnDestroy(): void {
    this.stopTyping(true);
  }

  // ── Composer ─────────────────────────────────────────────────────────────────

  onComposerEnter(event: Event): void {
    const kb = event as KeyboardEvent;
    if (kb.shiftKey) return;
    kb.preventDefault();
    void this.send();
  }

  onComposerInput(): void {
    this.growComposer();
    this.sendError.set(null);
    if (this.demoMode || !this.conversationId) return;

    const text = this.form.controls.text.value.trim();
    if (!text) { this.stopTyping(); return; }

    if (!this.typingActive) {
      this.typingActive = true;
      this.socket.sendTypingState({ conversationId: this.conversationId, isTyping: true });
    }
    this.clearTypingStopTimer();
    this.typingStopTimer = setTimeout(() => this.stopTyping(), 2000);
  }

  async onImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.conversationId) return;

    this.attachmentUploading.set(true);
    this.sendError.set(null);
    try {
      const att = await this.attachmentsApi.uploadToConversation(this.conversationId, file);
      this.pendingAttachment.set({ id: att.id, name: att.original_filename });
    } catch {
      this.sendError.set('Failed to upload image. Please try again.');
    } finally {
      this.attachmentUploading.set(false);
    }
  }

  removePendingAttachment(): void {
    this.pendingAttachment.set(null);
  }

  async send(): Promise<void> {
    const text = this.form.controls.text.value.trim();
    const attachment = this.pendingAttachment();
    if (!text && !attachment) return;

    if (this.demoMode) {
      this.demoMessageSent.emit({
        id: uuid(),
        conversation_id: 'preview-conversation',
        sender_type: 'OPERATOR',
        sender_user_id: 'preview-operator',
        text: text || '',
        meta: null,
        created_at: new Date().toISOString(),
      });
      this.form.reset();
      this.resetComposerHeight();
      this.pendingAttachment.set(null);
      return;
    }

    if (!this.conversationId) return;

    this.stopTyping(true);
    this.sendError.set(null);
    this.sending.set(true);

    try {
      await this.socket.connect();
      await this.socket.joinConversation(this.conversationId);
      await this.socket.sendMessage({
        conversationId: this.conversationId,
        text: text || '',
        clientMessageId: uuid(),
        attachmentIds: attachment ? [attachment.id] : undefined,
      });
      this.form.reset();
      this.resetComposerHeight();
      this.pendingAttachment.set(null);
    } catch (err) {
      this.sendError.set(this.humanizeSendError(err));
    } finally {
      this.sending.set(false);
    }
  }

  // ── Display helpers ───────────────────────────────────────────────────────────

  isSystemMessage(msg: ApiMessage): boolean {
    return msg.sender_type === 'SYSTEM';
  }

  isOperatorMessage(msg: ApiMessage): boolean {
    return msg.sender_type === 'OPERATOR';
  }

  messageSenderLabel(msg: ApiMessage): string {
    if (msg.sender_type === 'USER') return this.customerName;
    if (msg.sender_type === 'OPERATOR') return 'Operator';
    if (msg.sender_type === 'AI') return 'Eldara';
    return 'System';
  }

  messageAvatarLabel(msg: ApiMessage): string {
    if (msg.sender_type === 'USER') return this.customerInitials;
    if (msg.sender_type === 'OPERATOR') return 'OP';
    if (msg.sender_type === 'AI') return 'AI';
    return 'SY';
  }

  avatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
      hash |= 0;
    }
    return `hsl(${Math.abs(hash) % 360}, 55%, 40%)`;
  }

  // ── Private ───────────────────────────────────────────────────────────────────

  private growComposer(): void {
    const el = this.composerTextarea?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  private resetComposerHeight(): void {
    const el = this.composerTextarea?.nativeElement;
    if (el) el.style.height = 'auto';
  }

  private scheduleScroll(type: 'bottom' | 'unread', behavior: ScrollBehavior): void {
    setTimeout(() => {
      if (type === 'unread' && this.firstUnreadMessageId) {
        this.scrollToUnread(behavior);
      } else {
        this.scrollToBottom(behavior);
      }
    }, 0);
  }

  private scrollToBottom(behavior: ScrollBehavior): void {
    const el = this.messagesEl?.nativeElement;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }

  private scrollToUnread(behavior: ScrollBehavior): void {
    const container = this.messagesEl?.nativeElement;
    if (!container) return;
    const divider = container.querySelector('.unread-divider') as HTMLElement | null;
    if (divider) divider.scrollIntoView({ behavior, block: 'start' });
    else this.scrollToBottom(behavior);
  }

  private isNearBottom(threshold = 120): boolean {
    const el = this.messagesEl?.nativeElement;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }

  private clearTypingStopTimer(): void {
    if (this.typingStopTimer) {
      clearTimeout(this.typingStopTimer);
      this.typingStopTimer = null;
    }
  }

  private stopTyping(force = false): void {
    this.clearTypingStopTimer();
    if (!this.conversationId) { this.typingActive = false; return; }
    if (!this.typingActive && !force) return;
    this.typingActive = false;
    this.socket.sendTypingState({ conversationId: this.conversationId, isTyping: false });
  }

  private humanizeSendError(error: unknown): string {
    if (error instanceof Error) {
      if (error.message === 'Socket not connected') return 'Chat connection lost. Please wait and try again.';
      if (error.message === 'WS connect timeout') return 'Could not reconnect in time. Please try again.';
      return error.message;
    }
    return 'Failed to send the message. Please try again.';
  }
}
